import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Sprout, Users, MapPin, Clock, Calendar, BadgeCheck, MessageSquare, CalendarPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OrgVoices } from "@/components/OrgVoices";
import { OrgPinnedStories } from "@/components/OrgPinnedStories";
import { ReportButton } from "@/components/ReportButton";

export default function PublicOrg() {
  const { orgId } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.orgPublic(orgId).then(setD).catch(() => { setD(null); setNotFound(true); });
  }, [orgId]);

  if (notFound) {
    return (
      <div data-testid="org-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-3xl text-forest">Organisation not found</div>
        <p className="text-neutral-600 mt-3 text-sm">This organisation may no longer exist, or the link is wrong.</p>
        <Link to="/opportunities" data-testid="org-not-found-back" className="inline-block mt-6 text-forest font-medium underline">Find opportunities</Link>
      </div>
    );
  }
  if (!d) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading organisation…</div>;
  const o = d.org;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="public-org-page">
      <Seo title={o.org_name || o.name} description={`${o.org_name || o.name} on CivicHearth — ${d.total_hours} volunteer hours mobilised. See open opportunities and impact.`} />
      <Card className="border-neutral-200 overflow-hidden">
        <CardContent className="p-8 bg-gradient-to-br from-forest/[0.04] to-terracotta/[0.04]">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Organisation</div>
            {o.verified && (
              <Badge data-testid="org-verified-badge" className="bg-forest text-white border-none inline-flex items-center gap-1">
                <BadgeCheck className="w-3.5 h-3.5" /> Verified
              </Badge>
            )}
          </div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">{o.org_name || o.name}</h1>
          {o.location && <div className="text-sm text-neutral-600 mt-1 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" />{o.location}</div>}
          {o.bio && <p className="text-neutral-700 leading-relaxed mt-4 max-w-2xl">{o.bio}</p>}
          <div className="flex gap-2 mt-5 flex-wrap">
            {user && user.user_id !== o.user_id && (
              <Link to={`/messages?to=${o.user_id}`}>
                <Button className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm"><MessageSquare className="w-4 h-4 mr-1.5" />Message</Button>
              </Link>
            )}
            <Link to={`/o/${o.user_id}/impact`}>
              <Button variant="outline" className="rounded-full h-9 px-4 text-sm" data-testid="org-impact-link">Impact report</Button>
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/orgs/${o.user_id}/events.ics`}
              data-testid="org-subscribe-events"
            >
              <Button variant="outline" className="rounded-full h-9 px-4 text-sm">
                <CalendarPlus className="w-4 h-4 mr-1.5" /> Subscribe to our events
              </Button>
            </a>
            {user && user.user_id !== o.user_id && (
              <span className="self-center">
                <ReportButton targetType="user" targetId={o.user_id} label="Report" />
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
        <Stat label="Hours mobilised" value={d.total_hours} icon={Sprout} />
        <Stat label="Volunteers" value={d.volunteers} icon={Users} />
        <Stat label="Opportunities" value={d.opportunities_posted} />
        <Stat label="Open now" value={d.open_opportunities.length} />
      </div>

      {d.top_causes.length > 0 && (
        <>
          <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Where the hours went</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {d.top_causes.map((c) => (
              <Badge key={c.value} variant="outline" className="rounded-full border-forest/20 text-forest px-4 py-2 capitalize">
                {c.value} · {c.hours}h
              </Badge>
            ))}
          </div>
        </>
      )}

      <OrgVoices orgId={o.user_id} />
      <OrgPinnedStories orgId={o.user_id} />

      <h2 className="font-display text-2xl text-forest mt-10 tracking-tight">Open opportunities</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="org-open-opps">
        {d.open_opportunities.length === 0 && <div className="text-sm text-neutral-500">No open opportunities right now.</div>}
        {d.open_opportunities.map((op) => (
          <Link key={op.id} to={`/opportunities/${op.id}`} className="bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
            <div className="font-medium text-forest">{op.title}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{op.location}</div>
            <div className="flex gap-3 mt-3 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" />{op.hours_required}h</span>
              {op.event_date && (
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-forest/60" />{new Date(op.event_date).toLocaleDateString()}</span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-forest/60" />}
      </div>
      <div className="font-display text-3xl text-forest mt-2">{value}</div>
    </div>
  );
}
